/**
 * Shared UI atoms for the console. Small, composable, styled through the
 * design-system classes in index.css. The one non-obvious piece is <DataBlock>:
 * every dataset in the app is wrapped in it so it always offers a Table ⇄ Raw
 * JSON toggle — the raw view is the safety net that guarantees nothing upstream
 * is ever hidden by a column choice.
 */
import { useState, type ReactNode } from 'react';
import type { Numeric } from './lib/types';

export const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

// ---- status / source markers ---------------------------------------------

type DotKind = 'ok' | 'warn' | 'bad' | 'tec' | 'kro' | 'muted';
const DOT: Record<DotKind, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  bad: 'bg-bad',
  tec: 'bg-tec',
  kro: 'bg-kro',
  muted: 'bg-faint',
};
export function Dot({ kind }: { kind: DotKind }) {
  return <span className={cx('dot', DOT[kind])} />;
}

export function Chip({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cx('chip', className)}>{children}</span>;
}

/** Coloured source pill — TecDoc blue, Kromeda teal, anything else neutral. */
export function SourceChip({ source }: { source: string }) {
  const s = source.toLowerCase();
  const kind: DotKind = s === 'tecdoc' ? 'tec' : s === 'kromeda' ? 'kro' : 'muted';
  const label = s === 'tecdoc' ? 'TecDoc' : s === 'kromeda' ? 'Kromeda' : source;
  return (
    <Chip>
      <Dot kind={kind} />
      {label}
    </Chip>
  );
}

/** Curated status pill: confirmed (critical) vs suspected (warning). */
export function StatusChip({ status }: { status: 'confirmed' | 'suspected' }) {
  return (
    <span className={cx('chip', status === 'confirmed' ? 'border-bad text-bad' : 'border-warn text-warn')}>
      {status}
    </span>
  );
}

// ---- confidence meter -----------------------------------------------------

export function ConfBar({ value }: { value: Numeric }) {
  const v = typeof value === 'string' ? parseFloat(value) : value;
  const n = Number.isFinite(v) ? v : 0;
  return (
    <span className="confbar num">
      <i>
        <b style={{ width: `${Math.round(n * 100)}%` }} />
      </i>
      {n.toFixed(2)}
    </span>
  );
}

// ---- controls -------------------------------------------------------------

export function Switch({ on, onChange, children }: { on: boolean; onChange: (v: boolean) => void; children?: ReactNode }) {
  return (
    <button type="button" className={cx('switch', on && 'on')} onClick={() => onChange(!on)} aria-pressed={on}>
      <i />
      {children}
    </button>
  );
}

export interface SegOption<T extends string> {
  value: T;
  label: string;
}
export function Seg<T extends string>({
  options,
  value,
  onChange,
  mini,
}: {
  options: SegOption<T>[];
  value: T;
  onChange: (v: T) => void;
  mini?: boolean;
}) {
  return (
    <div className={cx('seg', mini && 'mini')}>
      {options.map((o) => (
        <button key={o.value} className={value === o.value ? 'on' : undefined} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---- layout helpers -------------------------------------------------------

export function Banner({ icon = '⚑', children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <div className="banner">
      <span aria-hidden>{icon}</span>
      <span>{children}</span>
    </div>
  );
}

export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('mb-3.5 flex flex-wrap items-center gap-2', className)}>{children}</div>;
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      {children}
    </label>
  );
}

export function Stub({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="stub">
      <b className="text-fg">{title}</b>
      {children && <div className="mt-2 text-[12.5px]">{children}</div>}
    </div>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return <div className="p-6 text-center text-[12.5px] text-muted">{label}</div>;
}

export function ErrorBox({ children }: { children: ReactNode }) {
  return (
    <div className="banner" style={{ borderLeftColor: 'var(--bad)', color: 'var(--bad)' }}>
      <span aria-hidden>⚠</span>
      <span>{children}</span>
    </div>
  );
}

// ---- table ----------------------------------------------------------------

export function Table({ cols, children }: { cols: ReactNode[]; children: ReactNode }) {
  return (
    <table className="tbl">
      <thead>
        <tr>
          {cols.map((c, i) => (
            <th key={i}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

export function Empty({ children = 'No rows.' }: { children?: ReactNode }) {
  return <div className="p-6 text-center text-[12.5px] text-faint">{children}</div>;
}

// ---- data block: Table ⇄ Raw JSON ----------------------------------------

export function DataBlock({
  title,
  sub,
  right,
  raw,
  children,
  tableLabel = 'Table',
  defaultRaw = false,
}: {
  title: ReactNode;
  sub?: ReactNode;
  right?: ReactNode;
  raw: unknown;
  children: ReactNode;
  tableLabel?: string;
  defaultRaw?: boolean;
}) {
  const [mode, setMode] = useState<'table' | 'raw'>(defaultRaw ? 'raw' : 'table');
  return (
    <div className="blk">
      <div className="blk-head">
        <h3 className="text-[13px] font-semibold">{title}</h3>
        {sub && <span className="text-[11.5px] text-faint">{sub}</span>}
        <div className="ml-auto flex items-center gap-2">
          {right}
          <div className="seg mini">
            <button className={mode === 'table' ? 'on' : undefined} onClick={() => setMode('table')}>
              {tableLabel}
            </button>
            <button className={mode === 'raw' ? 'on' : undefined} onClick={() => setMode('raw')}>
              Raw JSON
            </button>
          </div>
        </div>
      </div>
      {mode === 'table' ? (
        <div className="tblwrap">{children}</div>
      ) : (
        <pre className="raw">{JSON.stringify(raw, null, 2)}</pre>
      )}
    </div>
  );
}

// ---- pagination -----------------------------------------------------------

export function Pager({
  total,
  limit,
  offset,
  onOffset,
}: {
  total: number;
  limit: number;
  offset: number;
  onOffset: (o: number) => void;
}) {
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);
  return (
    <div className="flex items-center gap-2 text-[12px] text-muted num">
      <span>
        {from}–{to} of {total}
      </span>
      <button className="btn btn-sm" disabled={offset <= 0} onClick={() => onOffset(Math.max(0, offset - limit))}>
        Prev
      </button>
      <button className="btn btn-sm" disabled={to >= total} onClick={() => onOffset(offset + limit)}>
        Next
      </button>
    </div>
  );
}

// ---- modal ----------------------------------------------------------------

export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="panel w-[460px] max-w-full"
        style={{ boxShadow: 'var(--shadow)' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="blk-head">
          <h3 className="text-[13px] font-semibold">{title}</h3>
          <button className="btn btn-sm ml-auto" onClick={onClose}>
            Close ✕
          </button>
        </div>
        <div className="space-y-3 p-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-line p-3">{footer}</div>}
      </div>
    </div>
  );
}
